import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

// Tömeges cserélő komponens
interface BulkReplaceTabProps {
    gameId?: string;
    versionId?: string;
}

const BulkReplaceTab: React.FC<BulkReplaceTabProps> = ({ gameId, versionId }) => {
    const [filterTerm, setFilterTerm] = useState(""); // Szűrő/kereső mező
    const [searchLanguage, setSearchLanguage] = useState<"HU" | "EN">("HU"); // Keresési nyelv
    const [searchTerm, setSearchTerm] = useState(""); // Ezt cseréli
    const [replaceTerm, setReplaceTerm] = useState(""); // Erre cseréli
    const [activeFilterTerm, setActiveFilterTerm] = useState("");
    const [activeSearchLanguage, setActiveSearchLanguage] = useState<"HU" | "EN">("HU");
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [confirmEmpty, setConfirmEmpty] = useState(false); // Üresre csere megerősítése
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false); // Tömeges törlés megerősítése
    const [editingId, setEditingId] = useState<number | null>(null); // Szerkesztés azonosító
    const [editingContent, setEditingContent] = useState<string>(""); // Szerkesztés tartalma

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['bulkReplaceSearch', gameId, activeFilterTerm, activeSearchLanguage],
        queryFn: async () => {
            if (!activeFilterTerm) return { results: [], count: 0 };
            const response = await fetch(`/api/games/${gameId}/bulk_replace/search?search_term=${encodeURIComponent(activeFilterTerm)}&search_language=${activeSearchLanguage}`);
            if (!response.ok) throw new Error('Keresés sikertelen');
            return response.json();
        },
        enabled: !!gameId && !!activeFilterTerm,
    });

    const replaceMutation = useMutation({
        mutationFn: async () => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/games/${gameId}/bulk_replace/replace`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    search_term: searchTerm,
                    replace_term: replaceTerm,
                    translation_ids: Array.from(selectedIds),
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Csere sikertelen");
            }
            return response.json();
        },
        onSuccess: (data) => {
            toast({
                title: "Sikeres csere!",
                description: `${data.updated_count} fordítás frissítve.${data.errors?.length ? ` Hibák: ${data.errors.length}` : ''}`
            });
            setSelectedIds(new Set());
            setConfirmEmpty(false);
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Elfogadás mutation
    const acceptMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}/accept_translation`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Elfogadás sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás elfogadva." });
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Törlés mutation
    const deleteMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Törlés sikertelen");
            }
            return { success: true };
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás törölve." });
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Tömeges törlés mutation
    const bulkDeleteMutation = useMutation({
        mutationFn: async () => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");

            let successCount = 0;
            let errorCount = 0;

            for (const id of Array.from(selectedIds)) {
                try {
                    const response = await fetch(`/api/extracted_texts/${id}`, {
                        method: "DELETE",
                        headers: {
                            "X-CSRF-Token": token,
                            "Content-Type": "application/json",
                        },
                    });
                    if (response.ok || response.status === 204) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch {
                    errorCount++;
                }
            }

            return { successCount, errorCount };
        },
        onSuccess: (data) => {
            toast({
                title: "Tömeges törlés kész!",
                description: `${data.successCount} fordítás törölve.${data.errorCount > 0 ? ` Hibák: ${data.errorCount}` : ''}`
            });
            setSelectedIds(new Set());
            setConfirmBulkDelete(false);
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Szerkesztés mutation
    const updateMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    extracted_text: {
                        content: editingContent,
                    },
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Szerkesztés sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás szerkesztve." });
            setEditingId(null);
            setEditingContent("");
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    const handleSearch = () => {
        setActiveFilterTerm(filterTerm);
        setActiveSearchLanguage(searchLanguage);
        setSelectedIds(new Set());
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const toggleSelection = (id: number) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const selectAll = () => {
        if (data?.results) {
            setSelectedIds(new Set(data.results.map((r: any) => r.id)));
        }
    };

    const deselectAll = () => {
        setSelectedIds(new Set());
    };

    // Előnézet: hogyan fog kinézni a csere után
    const getPreview = (content: string) => {
        if (!searchTerm) return content;
        return content.replace(new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replaceTerm);
    };

    // Ellenőrizzük, hogy a csere gomb aktív lehet-e
    const isReplaceEnabled = selectedIds.size > 0 && searchTerm.length > 0 && (replaceTerm.length > 0 || confirmEmpty);

    return (
        <div className="space-y-4">
            {/* Fejléc */}
            <div>
                <h2 className="text-2xl font-bold">Tömeges cserélő</h2>
                <p className="text-muted-foreground">Magyar fordításokban szövegrészlet cseréje</p>
            </div>

            {/* Keresési mező */}
            <div className="flex gap-4 items-end flex-wrap p-4 border rounded-lg bg-muted/50">
                <div className="flex-1 min-w-[250px]">
                    <Label htmlFor="filterTerm" className="text-sm font-medium">Keres:</Label>
                    <Input
                        id="filterTerm"
                        placeholder="Keresendő szöveg a szűréshez..."
                        value={filterTerm}
                        onChange={(e) => setFilterTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="mt-1"
                    />
                </div>
                <div className="flex items-center gap-3">
                    <Label className="text-sm font-medium">Nyelv:</Label>
                    <label className="flex items-center gap-1 cursor-pointer">
                        <input
                            type="radio"
                            name="searchLang"
                            checked={searchLanguage === "HU"}
                            onChange={() => setSearchLanguage("HU")}
                            className="w-4 h-4"
                        />
                        <span>HU</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                        <input
                            type="radio"
                            name="searchLang"
                            checked={searchLanguage === "EN"}
                            onChange={() => setSearchLanguage("EN")}
                            className="w-4 h-4"
                        />
                        <span>EN</span>
                    </label>
                </div>
                <Button onClick={handleSearch}>Megkeres</Button>
            </div>

            {/* Csere mezők */}
            <div className="flex gap-4 items-end flex-wrap p-4 border rounded-lg bg-orange-50 dark:bg-orange-950/20">
                <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="searchTerm" className="text-sm font-medium">Ezt cseréli (HU-ban):</Label>
                    <Input
                        id="searchTerm"
                        placeholder="Cserélendő szöveg..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mt-1"
                    />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="replaceTerm" className="text-sm font-medium">Erre:</Label>
                    <Input
                        id="replaceTerm"
                        placeholder="Csere szöveg..."
                        value={replaceTerm}
                        onChange={(e) => setReplaceTerm(e.target.value)}
                        className="mt-1"
                    />
                </div>
                {replaceTerm.length === 0 && (
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="confirmEmpty"
                            checked={confirmEmpty}
                            onCheckedChange={(checked) => setConfirmEmpty(checked as boolean)}
                        />
                        <Label htmlFor="confirmEmpty" className="text-sm text-red-600 cursor-pointer">
                            Üresre cserélem (törlés)
                        </Label>
                    </div>
                )}
            </div>

            {/* Eredmények */}
            {isLoading && <p>Keresés...</p>}
            {isError && <p className="text-red-500">Hiba: {error?.message}</p>}

            {!activeFilterTerm && (
                <p className="text-muted-foreground text-sm">Írd be a keresőkifejezést és nyomj Entert vagy kattints a "Megkeres" gombra.</p>
            )}

            {data && data.count > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-base font-semibold">Találat: {data.count} fordítás | Kiválasztva: {selectedIds.size}</p>
                        <div className="flex gap-2 flex-wrap items-center">
                            <Button variant="outline" size="sm" onClick={selectAll}>Mind kijelöl</Button>
                            <Button variant="outline" size="sm" onClick={deselectAll}>Kijelölés törlése</Button>
                            <Button
                                onClick={() => replaceMutation.mutate()}
                                disabled={!isReplaceEnabled || replaceMutation.isPending}
                                className="bg-orange-500 hover:bg-orange-600"
                            >
                                {replaceMutation.isPending ? "Csere..." : `Csere (${selectedIds.size} db)`}
                            </Button>
                            <div className="flex items-center gap-1 ml-2 border-l pl-2">
                                <Checkbox
                                    id="confirmBulkDelete"
                                    checked={confirmBulkDelete}
                                    onCheckedChange={(checked) => setConfirmBulkDelete(!!checked)}
                                />
                                <label htmlFor="confirmBulkDelete" className="text-xs cursor-pointer">Megerősítem</label>
                            </div>
                            <Button
                                variant="destructive"
                                onClick={() => bulkDeleteMutation.mutate()}
                                disabled={selectedIds.size === 0 || !confirmBulkDelete || bulkDeleteMutation.isPending}
                            >
                                {bulkDeleteMutation.isPending ? "Törlés..." : `Törlés (${selectedIds.size} db)`}
                            </Button>
                        </div>
                    </div>

                    <div className="w-full space-y-2">
                        {data.results.map((item: any) => (
                            <div key={item.id} className={`border rounded-lg overflow-hidden ${selectedIds.has(item.id) ? 'bg-blue-50 dark:bg-blue-950' : 'hover:bg-muted/50'}`}>
                                {/* Felső sor - 4 oszlop */}
                                <div className="grid grid-cols-[48px_1fr_1fr_1fr_96px] gap-4 p-3 border-b">
                                    {/* Kijelölés */}
                                    <div className="flex items-center justify-center">
                                        <Checkbox
                                            checked={selectedIds.has(item.id)}
                                            onCheckedChange={() => toggleSelection(item.id)}
                                        />
                                    </div>

                                    {/* Angol (EN) */}
                                    <div className="break-words">
                                        <span className="text-sm">{item.en_content}</span>
                                    </div>

                                    {/* Magyar (HU) - Jelenlegi */}
                                    <div className={`break-words text-sm ${item.status === 'accepted' ? 'text-green-600' : 'text-yellow-600'}`}>
                                        {item.status === 'accepted' ? (
                                            <span className="font-semibold">✓ </span>
                                        ) : (
                                            <span>⏳ </span>
                                        )}
                                        {/* Kiemeljük a cserélendő részt */}
                                        {searchTerm ? item.hu_content.split(new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'g')).map((part: string, i: number) =>
                                            part === searchTerm ?
                                                <span key={i} className="bg-yellow-300 dark:bg-yellow-700 px-0.5 rounded">{part}</span> :
                                                part
                                        ) : item.hu_content}
                                    </div>

                                    {/* Magyar (HU) - Előnézet */}
                                    <div className="break-words text-sm text-green-600">
                                        {getPreview(item.hu_content)}
                                    </div>

                                    {/* Műveletek */}
                                    <div className="flex gap-1 justify-center items-center">
                                        {editingId !== item.id && (
                                            <>
                                                {item.status !== 'accepted' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-green-500 hover:text-green-700"
                                                        onClick={() => acceptMutation.mutate(item.id)}
                                                        disabled={acceptMutation.isPending}
                                                        title="Elfogad"
                                                    >
                                                        ✓
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 px-2 text-blue-500 hover:text-blue-700"
                                                    onClick={() => {
                                                        setEditingId(item.id);
                                                        setEditingContent(item.hu_content);
                                                    }}
                                                    title="Szerkesztés"
                                                >
                                                    ✏️
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 px-2 text-red-500 hover:text-red-700"
                                                    onClick={() => {
                                                        if (window.confirm("Biztosan törölni szeretnéd ezt a fordítást?")) {
                                                            deleteMutation.mutate(item.id);
                                                        }
                                                    }}
                                                    disabled={deleteMutation.isPending}
                                                    title="Törlés"
                                                >
                                                    🗑️
                                                </Button>
                                                <a
                                                    href={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${item.parent_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center h-7 px-2 text-blue-500 hover:text-blue-700"
                                                    title="Megnyit (új ablakban)"
                                                >
                                                    🔗
                                                </a>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Alsó sor - File név vagy Szerkesztés */}
                                {editingId === item.id ? (
                                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 space-y-2">
                                        <textarea
                                            value={editingContent}
                                            onChange={(e) => setEditingContent(e.target.value)}
                                            className="w-full p-2 border rounded text-sm font-mono min-h-24 resize-none bg-white dark:bg-slate-900 text-black dark:text-white border-gray-300 dark:border-slate-700"
                                            autoFocus
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                className="bg-green-500 hover:bg-green-600"
                                                onClick={() => updateMutation.mutate(item.id)}
                                                disabled={updateMutation.isPending || editingContent.trim() === ""}
                                            >
                                                {updateMutation.isPending ? "Mentés..." : "Mentés"}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setEditingId(null);
                                                    setEditingContent("");
                                                }}
                                                disabled={updateMutation.isPending}
                                            >
                                                Mégse
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-3 py-2 bg-muted/30 text-xs text-muted-foreground">
                                        <span className="font-mono">Fájl: {item.en_name}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {data && data.count === 0 && activeFilterTerm && (
                <p className="text-muted-foreground">Nincs találat a(z) "{activeFilterTerm}" kifejezésre a magyar fordításokban.</p>
            )}
        </div>
    );
};

const Translate = () => {
    const { gameId, versionId } = useParams<{ gameId: string; versionId: string }>();

    const { data: gameDetails } = useQuery({
        queryKey: ["gameDetails", gameId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz a játékhoz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId,
    });

    const { data: versionDetails } = useQuery({
        queryKey: ["versionDetails", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz a verzióhoz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    return (
        <div className="container mx-auto p-4 space-y-8">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}`}>{gameDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}/versions/${versionId}`}>Verzió: {versionDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>Tömeges cserélő</BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <VersionNavigation gameId={gameId} versionId={versionId} />
            <div className="py-8">
                <BulkReplaceTab gameId={gameId} versionId={versionId} />
            </div>
        </div>
    );
};

export default Translate;
